import { useEffect, useState, useCallback, Fragment } from 'react';
import api from '../services/api';
import useLang from '../hooks/useLang';

const PAGE_SIZE = 50;

export default function SmsLogs() {
  const { T } = useLang();
  const S = T.smsLogs;

  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [sentCount, setSent]    = useState(0);
  const [failedCount, setFailed] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [offset, setOffset]     = useState(0);
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [from, setFrom]         = useState('');
  const [to, setTo]             = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback((off = 0) => {
    setLoading(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: off });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (from)   params.set('from', from);
    if (to)     params.set('to', to);
    api.get(`/sms-logs?${params}`)
      .then(r => {
        setRows(r.data.rows || []);
        setTotal(r.data.total || 0);
        setSent(r.data.sent_count || 0);
        setFailed(r.data.failed_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, status, from, to]);

  useEffect(() => { setOffset(0); load(0); }, [search, status, from, to, load]);

  const goPage = (newOffset) => { setOffset(newOffset); load(newOffset); };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  function fmt(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('sw-TZ', { dateStyle: 'short', timeStyle: 'short' });
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{S.title}</h1>
      <p className="text-sm text-gray-500 mb-4">{S.subtitle}</p>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">{S.totalRecords}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{total}</p>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-4">
          <p className="text-xs text-green-500 uppercase tracking-wide">{S.sent}</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{sentCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-4">
          <p className="text-xs text-red-500 uppercase tracking-wide">{S.failed}</p>
          <p className="text-2xl font-bold text-red-700 mt-1">{failedCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder={S.searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[160px]"
        />
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">{S.allStatus}</option>
          <option value="sent">{S.sent}</option>
          <option value="failed">{S.failed}</option>
        </select>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>{S.from}:</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>{S.to}:</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        {(search || status || from || to) && (
          <button onClick={() => { setSearch(''); setStatus(''); setFrom(''); setTo(''); }}
            className="text-sm text-red-600 hover:underline">
            {S.clearFilters}
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">{S.loading}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">{S.noData}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{S.col.time}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{S.col.phone}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{S.col.message}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{S.col.status}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(row => (
                  <Fragment key={row.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">{fmt(row.created_at)}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{row.phone}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-md truncate">{row.message}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${
                          row.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {row.status === 'sent' ? S.sent : S.failed}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                          className="text-xs text-blue-500 hover:underline"
                        >
                          {expanded === row.id ? S.hideDetails : S.viewDetails}
                        </button>
                      </td>
                    </tr>
                    {expanded === row.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="text-xs space-y-1">
                            <p><span className="font-semibold text-gray-600">{S.col.message}:</span> {row.message}</p>
                            {row.error && (
                              <p className="text-red-600"><span className="font-semibold">{S.col.error}:</span> {row.error}</p>
                            )}
                            {row.http_code && (
                              <p><span className="font-semibold text-gray-600">HTTP:</span> {row.http_code}</p>
                            )}
                            {row.provider_response && (
                              <pre className="bg-white border border-gray-200 rounded-lg p-2 mt-1 overflow-x-auto text-[11px] text-gray-600">
                                {row.provider_response}
                              </pre>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            disabled={currentPage === 1}
            onClick={() => goPage(offset - PAGE_SIZE)}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
          >
            ← {S.prev}
          </button>
          <span className="text-sm text-gray-600">
            {S.page} {currentPage} / {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => goPage(offset + PAGE_SIZE)}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
          >
            {S.next} →
          </button>
        </div>
      )}
    </div>
  );
}
